import React from 'react';
import { NavLink, Link } from 'react-router-dom';
import {
  BookText, Home, Library, PenLine, Plus,
  Settings, BarChart3, PanelLeft, LogOut
} from 'lucide-react';
import { useAppData } from '../context/AppDataContext';
import { calculateBookProgress } from '../utils/bookUtils';
import { BOOK_STATUS, getBookStatus } from '../constants/bookStatus';

const MENU = [
  { to: '/', label: '홈', icon: Home, end: true },
  { to: '/library', label: '서재', icon: Library },
  { to: '/stats', label: '통계', icon: BarChart3 },
];

const navClass = ({ isActive }) =>
  [
    // min-w-0 이 없으면 긴 책 제목이 항목을 밀어내 사이드바 밖까지 번진다
    'flex min-w-0 items-center gap-3 px-3 py-2.5 rounded-md text-sm font-sans transition-colors duration-150',
    isActive
      ? 'bg-ink-100 dark:bg-ink-900 text-ink-900 dark:text-ink-50 font-medium'
      : 'text-ink-600 dark:text-ink-400 hover:text-ink-900 dark:hover:text-ink-100'
  ].join(' ');

const Sidebar = ({ onNavigate, onOpenQuickRecord, onCollapse }) => {
  const { books, user, onLogout, userSettings } = useAppData();

  // 읽는 중인 책만 사이드바에 노출 (status 미지정 레거시 문서는 '읽는 중'으로 간주)
  const readingBooks = books.filter(b => getBookStatus(b) === BOOK_STATUS.READING);

  return (
    <div className="flex h-full w-full min-w-0 flex-col bg-white dark:bg-ink-950">
      {/* 로고 */}
      <div className="flex items-center justify-between px-4 h-14 shrink-0">
        <Link
          to="/"
          onClick={onNavigate}
          className="flex items-center gap-2 text-ink-900 dark:text-ink-50"
        >
          <BookText size={18} strokeWidth={1.5} />
          <span className="font-display text-lg leading-none">북러닝</span>
        </Link>
        {onCollapse && (
          <button
            onClick={onCollapse}
            className="hidden lg:block p-1.5 text-ink-500 dark:text-ink-400 hover:text-ink-900 dark:hover:text-ink-100 transition-colors"
            aria-label="사이드바 접기"
          >
            <PanelLeft size={16} strokeWidth={1.5} />
          </button>
        )}
      </div>

      {/* 주 액션 */}
      <div className="grid grid-cols-2 gap-2 px-3 pb-4 shrink-0">
        <button onClick={onOpenQuickRecord} className="btn-primary !px-2 text-xs">
          <PenLine size={14} strokeWidth={1.5} />
          독서 기록
        </button>
        <Link to="/add" onClick={onNavigate} className="btn-ghost !px-2 text-xs">
          <Plus size={14} strokeWidth={1.5} />
          도서 추가
        </Link>
      </div>

      {/* 메뉴 + 내 서재 */}
      <nav className="flex-1 overflow-y-auto px-3 pb-4 scrollbar-hide">
        <p className="label px-3 pb-2">메뉴</p>
        <ul className="space-y-0.5">
          {MENU.map(({ to, label, icon: Icon, end }) => (
            <li key={to}>
              <NavLink to={to} end={end} onClick={onNavigate} className={navClass}>
                <Icon size={16} strokeWidth={1.5} />
                {label}
              </NavLink>
            </li>
          ))}
        </ul>

        <p className="label px-3 pt-6 pb-2">내 서재</p>
        {readingBooks.length === 0 ? (
          <p className="px-3 text-xs text-ink-500 dark:text-ink-400 font-sans">
            읽는 중인 책이 없어요
          </p>
        ) : (
          <ul className="space-y-0.5">
            {readingBooks.map(book => (
              <li key={book.id}>
                <NavLink
                  to={`/book/${book.id}`}
                  onClick={onNavigate}
                  className={navClass}
                  title={book.title}
                >
                  {book.imageUrl ? (
                    <img
                      src={book.imageUrl}
                      alt=""
                      className="w-5 h-7 object-cover rounded-[2px] shrink-0"
                    />
                  ) : (
                    <span className="w-5 h-7 rounded-[2px] shrink-0 border border-ink-200 dark:border-ink-800" />
                  )}
                  <span className="min-w-0 flex-1 truncate">{book.title}</span>
                  <span className="text-[11px] text-ink-500 dark:text-ink-400 shrink-0 tabular-nums">
                    {calculateBookProgress(book)}%
                  </span>
                </NavLink>
              </li>
            ))}
          </ul>
        )}
      </nav>

      {/* 하단 */}
      <div className="px-3 pb-4 shrink-0 space-y-0.5">
        <NavLink to="/settings" onClick={onNavigate} className={navClass}>
          <Settings size={16} strokeWidth={1.5} />
          설정
        </NavLink>

        <div className="flex items-center gap-2.5 px-3 pt-3 mt-2 border-t border-ink-200 dark:border-ink-800">
          {user?.photoURL ? (
            <img src={user.photoURL} alt="" className="w-7 h-7 rounded-full shrink-0" />
          ) : (
            <span className="w-7 h-7 rounded-full shrink-0 bg-ink-200 dark:bg-ink-800" />
          )}
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium text-ink-900 dark:text-ink-100 truncate font-sans">
              {userSettings?.nickname || '독자'}
            </p>
            <p className="text-[11px] text-ink-500 dark:text-ink-400 truncate font-sans">
              {user?.email}
            </p>
          </div>
          <button
            onClick={onLogout}
            className="p-1.5 text-ink-500 dark:text-ink-400 hover:text-ink-900 dark:hover:text-ink-100 transition-colors shrink-0"
            aria-label="로그아웃"
            title="로그아웃"
          >
            <LogOut size={14} strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
